import { Injectable } from '@angular/core';
import { AppConfig } from '../../shared/config/app-config';
import { ConsolidationDetail } from '../../shared/models/consolidation-detail';
import { ConsolidationLgas } from '../../shared/models/consolidation-lgas';
import { CredentialValidate } from '../../shared/models/credential-validate';
import { ApiClientHelper } from '../helpers/api-client.helper';
import { LocalStorageService } from './local-storage.service';
import {
    AddressComponent,
    AddressGeolocation,
    AddressPayload,
    AddressMatchingPayload,
    AddressPredictionResponse,
    AddressReValidationStep,
    GeocodingResponse,
    GeocodingResult,
    GeolocationType,
    PhysicalAddressPayload,
    RevalidationAddressType,
} from '../../kamona/model/kamonal-model';
import { OnboardingAddress } from '../models/onboarding/onboarding-v2-model';

@Injectable({
    providedIn: 'root',
})
export class ComplianceService {
    readonly GEOLOCATION_OPTIONS_TIMEOUT = 5000;
    readonly MAXIMUM_AGE = 0;
    constructor(private apiClientHelper: ApiClientHelper, public config: AppConfig, private localStorage: LocalStorageService) {}

    getGeolocationOptions() {
        return {
            enableHighAccuracy: false,
            timeout: this.GEOLOCATION_OPTIONS_TIMEOUT,
            maximumAge: this.MAXIMUM_AGE,
        };
    }

    getAddressVerificationSpan() {
        return {
            minimumAddressVerificationTimeInDays: this.config.MINIMUM_ADDRESS_VERIFICATION_TIME_IN_DAYS,
            maximumAddressVerificationTimeInDays: this.config.MAXIMUM_ADDRESS_VERIFICATION_TIME_IN_DAYS,
        };
    }

    isResidentialAddressType(addressType: string) {
        const formatedAddressType = addressType?.toLowerCase();
        return formatedAddressType === RevalidationAddressType.RESIDENTIAL;
    }

    getFormatedAddress(addressComponents: AddressComponent[]) {
        const city = this.getCity(addressComponents);
        return `${this.getStreetNumber(addressComponents)} ${this.getRoute(addressComponents)} ${city ? `${city},` : ``} ${this.getLga(
            addressComponents,
        )} ${this.getState(addressComponents)}`;
    }

    getCountry(addressComponents: AddressComponent[]) {
        const country = addressComponents.find((x) => this.hasGeolocationType(x.types, GeolocationType.COUNTRY))?.longName;
        return country ? `${country}` : '';
    }

    getLga(addressComponents: AddressComponent[]) {
        const lga = this.getAdministrativeAreaLevel2(addressComponents);
        return lga ? `${lga},` : '';
    }

    getAdministrativeAreaLevel2(addressComponents: AddressComponent[]) {
        const lga = addressComponents.find((x) => this.hasGeolocationType(x.types, GeolocationType.ADMINISTRATIVE_AREA_LEVEL_2))?.longName;
        return lga || '';
    }

    getState(addressComponents: AddressComponent[]) {
        const state = this.getAdministrativeAreaLevel1(addressComponents);
        return state ? `${state} State` : '';
    }

    getCountryFlagUrl(countryCode: string) {
        const baseUrl = this.config.COUNTRY_FLAGS_URL
        return `${baseUrl}${countryCode.toLowerCase()}.svg`
    }

    getAdministrativeAreaLevel1(addressComponents: AddressComponent[]) {
        const state = addressComponents.find((x) => this.hasGeolocationType(x.types, GeolocationType.ADMINISTRATIVE_AREA_LEVEL_1))?.longName;
        return state || '';
    }

    getHouseNo(addressComponents: AddressComponent[]) {
        const streetNo = addressComponents.find((x) => this.hasGeolocationType(x.types, GeolocationType.STREET_NUMBER))?.longName;
        return streetNo || '';
    }

    getStreetNumber(addressComponents: AddressComponent[]) {
        const streetNumber = this.getHouseNo(addressComponents);
        const alphaNumeric = new RegExp(/^[a-zA-Z0-9]+$/);
        if (alphaNumeric.test(streetNumber)) return streetNumber;
        return '';
    }

    hasGeolocationType(types: string[], geolocationType: GeolocationType) {
        return types.includes(geolocationType);
    }

    getStreetName(addressComponents: AddressComponent[]) {
        let streetNo = addressComponents.find((x) => this.hasGeolocationType(x.types, GeolocationType.STREET_ADDRESS))?.shortName;
        if (!streetNo) streetNo = addressComponents.find((x) => this.hasGeolocationType(x.types, GeolocationType.ROUTE))?.shortName;
        return streetNo ? `${streetNo}` : '';
    }

    getRoute(addressComponents: AddressComponent[]) {
        let route = this.getStreetName(addressComponents);
        const neighborhood = this.getNeighborhood(addressComponents);
        if (neighborhood) route = `${route}, ${neighborhood}`;
        return route;
    }

    getCity(addressComponents: AddressComponent[]) {
        const city = addressComponents.find((x) => this.hasGeolocationType(x.types, GeolocationType.LOCALITY))?.longName;
        return city ? `${city}` : '';
    }

    getNeighborhood(addressComponents: AddressComponent[]) {
        const neighborhood = addressComponents.find((x) => this.hasGeolocationType(x.types, GeolocationType.NEIGHBORHOOD))?.longName;
        return neighborhood ? `${neighborhood}` : '';
    }

    saveAddressOnFile(addressOnFile: AddressGeolocation) {
        if (!addressOnFile) {
            return;
        }
        this.localStorage.save('address_on_file', JSON.stringify(addressOnFile));
    }

    getAddressOnFile() {
        const file = this.getParseValue(this.localStorage.get('address_on_file'));
        if (!file) {
            this.localStorage.deleteValue('address_on_file');
        }
        return file;
    }

    clearRevalidationAddresses() {
        this.localStorage.deleteValue('address_on_file');
        this.localStorage.deleteValue('current_address');
    }

    clearRevalidationAForm() {
        this.clearRevalidationAddresses();
        this.localStorage.deleteValue('previous_address_revalidation_form');
    }

    setPreviousForm(form: AddressReValidationStep) {
        if (!form) {
            return;
        }
        this.localStorage.save('previous_address_revalidation_form', JSON.stringify(form));
    }

    getPreviousForm() {
        const form = this.getParseValue(this.localStorage.get('previous_address_revalidation_form'));
        if (!form) {
            this.localStorage.deleteValue('previous_address_revalidation_form');
            return AddressReValidationStep.PLACE_OF_RESIDENCE;
        }
        return form;
    }

    saveCurrentAddress(currentAddress: AddressGeolocation) {
        if (!currentAddress) {
            return;
        }
        this.localStorage.save('current_address', JSON.stringify(currentAddress));
    }

    getCurrentAddress() {
        const file = this.getParseValue(this.localStorage.get('current_address'));
        if (!file) {
            this.localStorage.deleteValue('current_address');
        }
        return file;
    }

    getParseValue(object: any) {
        let result = null;
        try {
            result = JSON.parse(object);
        } catch (e) {
            console.log(e);
        }
        return result;
    }

    getComplianceStatus(userType: string): Promise<ConsolidationDetail> {
        const legacyUrl = `${this.config.MONIEPOINT_HOST}/api/v1/${userType}/consolidation/status`;
        const currentUrl = `${this.config.MONIEPOINT_HOST}/api/v1/partner/consolidation/status?partnerType=${userType.toUpperCase()}`;
        const url = this.config.USE_NEW_COMPLIANCE_URLS ? currentUrl : legacyUrl;
        return this.apiClientHelper.getForPromise(url);
    }

    validateBVN(userType: string, params): Promise<{ validationKey: string }> {
        const legacyUrl = `${this.config.MONIEPOINT_HOST}/api/v1/${userType}/consolidation/bvn/validate`;
        const currentUrl = `${this.config.MONIEPOINT_HOST}/api/v1/partner/consolidation/bvn/validate?partnerType=${userType.toUpperCase()}`;
        const url = this.config.USE_NEW_COMPLIANCE_URLS ? currentUrl : legacyUrl;
        return this.apiClientHelper.newPostAsFormDataForPromise(url, params);
    }

    consolidationLGA(userType: string, params): Promise<ConsolidationLgas> {
        const legacyUrl = `${this.config.MONIEPOINT_HOST}/api/v1/${userType}/consolidation/lgas`;
        const currentUrl = `${this.config.MONIEPOINT_HOST}/api/v1/partner/consolidation/lgas?partnerType=${userType.toUpperCase()}`;
        const url = this.config.USE_NEW_COMPLIANCE_URLS ? currentUrl : legacyUrl;
        return this.apiClientHelper.postForPromise(url, params);
    }

    mapLGAS(userType: string, mappedLGAS): Promise<ConsolidationLgas> {
        const url = `${this.config.MONIEPOINT_HOST}/api/v1/${userType}/consolidation/location`;
        return this.apiClientHelper.postForPromise(url, mappedLGAS);
    }

    saveAddress(userType: string, data): Promise<ConsolidationLgas> {
        const legacyUrl = `${this.config.MONIEPOINT_HOST}/api/v1/${userType}/consolidation/address`;
        const currentUrl = `${this.config.MONIEPOINT_HOST}/api/v1/partner/consolidation/address?partnerType=${userType.toUpperCase()}`;
        const url = this.config.USE_NEW_COMPLIANCE_URLS ? currentUrl : legacyUrl;
        return this.apiClientHelper.postForPromise(url, data);
    }

    validateCredential(userType: string, params): Promise<CredentialValidate> {
        const url = `${this.config.MONIEPOINT_HOST}/api/v1/${userType}/consolidation/credentials/validate`;
        return this.apiClientHelper.postForPromise(url, params);
    }

    getAgreementForm(): Promise<{ template: string }> {
        const url = `${this.config.MONIEPOINT_HOST}/api/v1/partner/consolidation/agreement`;
        return this.apiClientHelper.getForPromise(url);
    }

    validateOTP(userType: string, params): Promise<any> {
        const url = `${this.config.MONIEPOINT_HOST}/api/v1/${userType}/consolidation/otp/validate`;
        return this.apiClientHelper.postForPromise(url, params);
    }

    saveAgreement(userType: string, params): Promise<any> {
        const legacyUrl = `${this.config.MONIEPOINT_HOST}/api/v1/${userType}/consolidation/agreement`;
        const currentUrl = `${this.config.MONIEPOINT_HOST}/api/v1/partner/consolidation/agreement?partnerType=${userType.toUpperCase()}`;
        const url = this.config.USE_NEW_COMPLIANCE_URLS ? currentUrl : legacyUrl;
        return this.apiClientHelper.postForPromise(url, params);
    }

    claimProfile(userType: string, params): Promise<any> {
        const url = `${this.config.MONIEPOINT_HOST}/api/v1/${userType}/consolidation/claim-profile`;
        return this.apiClientHelper.postForPromise(url, params);
    }

    getEducationalLevels(userType: string): Promise<any> {
        const legacyUrl = `${this.config.MONIEPOINT_HOST}/api/v1/${userType}/consolidation/education-levels`;
        const currentUrl = `${this.config.MONIEPOINT_HOST}/api/v1/partner/consolidation/education-levels?partnerType=${userType.toUpperCase()}`;
        const url = this.config.USE_NEW_COMPLIANCE_URLS ? currentUrl : legacyUrl;
        return this.apiClientHelper.getForPromise(url);
    }

    postEducationalLevels(userType: string, params): Promise<any> {
        const legacyUrl = `${this.config.MONIEPOINT_HOST}/api/v1/${userType}/consolidation/education-levels`;
        const currentUrl = `${this.config.MONIEPOINT_HOST}/api/v1/partner/consolidation/education-levels?partnerType=${userType.toUpperCase()}`;
        const url = this.config.USE_NEW_COMPLIANCE_URLS ? currentUrl : legacyUrl;
        return this.apiClientHelper.postForPromise(url, params);
    }

    getClaimReasons() {
        const url = `${this.config.MONIEPOINT_HOST}/api/v1/brm/consolidation/claim-profile/reasons`;
        return this.apiClientHelper.getForPromise(url);
    }

    createMetaMapReferenceForLivelinessCheckFirstStage(userType: string): Promise<any> {
        const legacyUrl = `${this.config.MONIEPOINT_HOST}/api/v1/brm/verifications/start/first-stage`;
        const currentUrl = `${this.config.MONIEPOINT_HOST}/api/v1/partner/verifications/start/first-stage?partnerType=${userType.toUpperCase()}`;
        const url = this.config.USE_NEW_COMPLIANCE_URLS ? currentUrl : legacyUrl;
        return this.apiClientHelper.postForPromise(url, {});
    }

    getLivelinessStatus(reference: string, stage: string, userType: string): Promise<any> {
        const legacyUrl = `${this.config.MONIEPOINT_HOST}/api/v1/brm/verifications/${reference}/status?stage=${stage}`;
        const currentUrl = `${this.config.MONIEPOINT_HOST}/api/v1/partner/verifications/${reference}/status?stage=${stage}&partnerType=${userType.toUpperCase()}`;
        const url = this.config.USE_NEW_COMPLIANCE_URLS ? currentUrl : legacyUrl;
        return this.apiClientHelper.getForPromise(url);
    }

    createMetaMapReferenceForLivelinessCheckForSC(stage: string, id: number, userType: string): Promise<any> {
        const legacyUrl = `${this.config.MONIEPOINT_HOST}/api/v1/brm/verifications/start/${stage}/${id}`;
        const currentUrl = `${this.config.MONIEPOINT_HOST}/api/v1/partner/verifications/start/${stage}/${id}?partnerType=${userType.toUpperCase()}`;
        const url = this.config.USE_NEW_COMPLIANCE_URLS ? currentUrl : legacyUrl;
        return this.apiClientHelper.postForPromise(url, {});
    }

    onBoardBrmOnFVS(stateCode: string, lgaCodes: string[]) {
        const url = `${this.config.MONIEPOINT_HOST}/api/v1/fvs/onboard`;

        return this.apiClientHelper.postForPromise(url, { stateCode, lgaCodes });
    }

    useGoogleMapJavascriptApi() {
        return this.config.USE_GOOGLE_MAP_JAVASCRIPT_API;
    }

    geolocationThreshold() {
        return this.config.GEOLOCATION_THRESHOLD_IN_METER;
    }

    geocodePredictions(value: string): Promise<AddressPredictionResponse> {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/maps/geocode/predictions?input=${value}`;
        return this.apiClientHelper.getForPromise(url);
    }

    geoCoding(address: string): Promise<GeocodingResponse> {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/maps/geocode?address=${encodeURIComponent(address)}`;
        return this.apiClientHelper.getForPromise(url);
    }

    reverseGecoding(latitude: number, longitude: number): Promise<GeocodingResponse> {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/maps/geocode?latitude=${latitude}&longitude=${longitude}`;
        return this.apiClientHelper.getForPromise(url);
    }

    reverseGecodingByPlaceId(placeId: string): Promise<GeocodingResponse> {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/maps/geocode?placeId=${placeId}`;
        return this.apiClientHelper.getForPromise(url);
    }

    matchAddress(payload: AddressMatchingPayload) {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/maps/address-match`;
        return this.apiClientHelper.postForPromise(url, payload);
    }

    useResidentialAddressVerificationForBusiness(appliesToAllAddressTypes: boolean) {
        const payload = { appliesToAllAddressTypes };
        const url = `${this.config.MONIEPOINT_ONBOARDING}/api/v1/physical-address-verification`;
        return this.apiClientHelper.putForPromise(url, payload);
    }

    submitPhysicalAddress(payload: PhysicalAddressPayload) {
        const url = `${this.config.MONIEPOINT_ONBOARDING}/api/v1/physical-address-verification`;
        return this.apiClientHelper.postForPromise(url, payload);
    }

    submitBoPhysicalAddressAsBrm(payload: PhysicalAddressPayload, agentManagerCode) {
        const url = `${this.config.MONIEPOINT_ONBOARDING}/api/v1/physical-address-verification?agentManagerCode=${agentManagerCode}`;
        return this.apiClientHelper.postForPromise(url, payload);
    }

    createAddress(payload: AddressPayload, username: string, headerParams: Record<string,string>) {
        const url = `${this.config.MONIEPOINT_AVS}/api/v1/addresses/user/${username}`;
        return this.apiClientHelper.postForPromise(url, payload,true,true,true,this.config.clientId, headerParams);
    }

    createGeolocationFromPhysicalAddress(res: GeocodingResult, physicalAddress: OnboardingAddress, displayAddress: string) {
        const location = res?.geometry?.location;
        const addressComponents = res?.addressComponents;
        const geolocation: AddressGeolocation = { latitude: location.lat, longitude: location.lng };
        geolocation.addressLineOne = displayAddress;
        geolocation.lga = physicalAddress?.lga?.name;
        geolocation.state = physicalAddress?.state?.name;
        geolocation.city = physicalAddress?.city;
        geolocation.streetName = physicalAddress?.addressLineOne;
        geolocation.country = physicalAddress?.country?.name;
        geolocation.houseNo = this.getStreetNumber(addressComponents);
        geolocation.proofOfAddressSubmitted = physicalAddress?.proofOfAddressSubmitted;
        geolocation.canVerifyBusinessWithResidentialAddress = physicalAddress?.canVerifyBusinessWithResidentialAddress;
        return geolocation;
    }
}
